import { last } from 'lodash'
import { Node as ProseNode } from 'prosemirror-model'
import { type EditorView } from 'prosemirror-view'
import { matchPath } from 'react-router-dom'

import { getLocationUrl } from 'hooks/useHistory'
import { DOC_PATH, FOLDER_DOC_PATH, HANDBOOK_DOC_PATH, HANDBOOK_PATH } from 'paths'
import { UrlString } from 'types/urlString'
import { isDevOrTest } from 'utils/nodeEnv'
import { getArticlePath } from 'utils/urlUtils'
import { SmartLinkAttributes } from './types'

export const createArticleMention = (view: EditorView, key: string, title: string, sectionId?: string): ProseNode => {
  const { schema } = view.state
  const hash = sectionId ? `#${sectionId}` : ``
  return schema.nodes.smartLink.create(
    {
      type: 'Article',
      title: title,
      url: getArticlePath({ slug: key }) + hash,
      key: key,
    },
    schema.text(title),
  )
}

export const createUserMention = (view: EditorView, url: string, userId: string, title: string): ProseNode => {
  const { schema } = view.state
  return schema.nodes.smartLink.create(
    {
      type: 'User',
      title: title,
      url: url,
      userId: `${userId}`,
    },
    schema.text(title),
  )
}

export const createHandbookMention = (view: EditorView, url: string, key: string, title: string): ProseNode => {
  const { schema } = view.state
  return schema.nodes.smartLink.create(
    {
      type: 'Handbook',
      title: title,
      url,
      key: key,
    },
    schema.text(title),
  )
}

export const createFolderMention = (view: EditorView, url: string, key: string, title: string): ProseNode => {
  const { schema } = view.state
  return schema.nodes.smartLink.create(
    {
      type: 'Folder',
      title: title,
      url,
      key: key,
    },
    schema.text(title),
  )
}

export function createMentionSmartLink(view: EditorView, attributes: SmartLinkAttributes): ProseNode {
  const { type, url, key, title, userId } = attributes

  switch (type) {
    case 'Article':
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return createArticleMention(view, key!, title)
    case 'Folder':
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return createFolderMention(view, url, key!, title)
    case 'Handbook':
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return createHandbookMention(view, url, key!, title)
    default:
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return createUserMention(view, url, userId!, title)
  }
}

const matchesHostInDevOrTest = (host: string) => {
  if (!isDevOrTest()) return false
  return getLocationUrl().host.includes('localhost') && host.includes('localhost')
}

export const maybeGetArticleKey = (url: UrlString | null) => {
  if (!url) return null

  let pathname, host

  try {
    ;({ pathname, host } = new URL(url))
  } catch (_) {
    return null
  }

  if (getLocationUrl().host !== host && !matchesHostInDevOrTest(host)) return null

  const articleUuid =
    matchPath<{ slug: string }>(pathname, { path: DOC_PATH })?.params.slug ??
    matchPath<{ doc: string }>(pathname, { path: [FOLDER_DOC_PATH, HANDBOOK_PATH, HANDBOOK_DOC_PATH] })?.params.doc
  if (articleUuid) {
    return last(articleUuid.split('-'))
  }

  return null
}

export const maybeGetSectionLink = (url: UrlString | null) => {
  if (!url) return null
  try {
    const { hash } = new URL(url)
    return hash
  } catch (_) {
    return null
  }
}
